/**
 * The signed-in user's pages: the standard ones every owner has, then the ones
 * the assistant built for them, newest first. Each row carries an icon for what
 * it shows, so the list reads at a glance.
 */
import {
  Link,
} from 'react-router-dom'
import {
  useEffect, useState,
} from 'react'
import {
  keepPreviousData, useQuery,
} from '@tanstack/react-query'
import {
  CalendarDays, LayoutDashboard, ListTodo, Loader2, Mail, MessagesSquare, ScrollText, Search,
} from 'lucide-react'
import type {
  LucideIcon,
} from 'lucide-react'
import type {
  AssistantViewSummary,
} from '../../client'
import {
  assistantViewService,
} from '../../services/api'
import {
  viewPath,
} from '../../utils/routes'
import {
  ViewShell,
} from './ViewShell'

const SEARCH_DELAY_MS = 250

/**
 * The icon for each standard page, by its default key; others get the page icon.
 * The keys are the default pages in backend/src/app/services/assistant_view_defaults.py:
 * a new one there needs an icon here, or it quietly gets the page icon.
 */
const DEFAULT_PAGE_ICONS: Record<string, LucideIcon> = {
  external_conversations: MessagesSquare,
  assistant_mailbox: Mail,
  open_todos: ListTodo,
  rules: ScrollText,
  calendar: CalendarDays,
}

function PageRow({ view }: { view: AssistantViewSummary }): JSX.Element {
  const Icon = (view.default_key ? DEFAULT_PAGE_ICONS[view.default_key] : undefined) ?? LayoutDashboard
  return (
    <li>
      <Link
        to={viewPath(view.id) ?? '#'}
        className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-gray-900 break-words">{view.title}</span>
          {view.subtitle && <span className="block mt-0.5 text-sm text-gray-500 break-words">{view.subtitle}</span>}
        </span>
        {!view.default && (
          <time className="shrink-0 text-xs text-gray-500">
            {new Date(view.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </time>
        )}
      </Link>
    </li>
  )
}

function PageSection({ title, views }: { title: string, views: AssistantViewSummary[] }): JSX.Element | null {
  if (views.length === 0) return null
  return (
    <section className="space-y-2">
      <h2 className="px-1 text-sm font-medium text-gray-500">{title}</h2>
      <ul className="bg-white rounded-2xl border border-gray-200 shadow-soft divide-y divide-gray-100">
        {views.map((view) => <PageRow key={view.id} view={view} />)}
      </ul>
    </section>
  )
}

export function ViewsIndexPage(): JSX.Element {
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [typed])
  const query = useQuery({
    queryKey: ['assistant-views', search],
    queryFn: () => assistantViewService.list(search),
    placeholderData: keepPreviousData,
  })
  const index = query.data
  const searching = search !== ''
  // Keep an old empty result from reading as "no match" for the new term.
  const settled = !query.isPlaceholderData
  return (
    <ViewShell>
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Your pages</h1>
          <p className="mt-1 text-gray-500">Your standard pages, and the ones your assistant made for you. Each one shows what&apos;s true when you open it.</p>
        </header>
        {(searching || (index && index.total > 0)) && (
          <label className="relative block">
            <span className="sr-only">Search your pages</span>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
            <input
              type="search"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search by title"
              maxLength={200}
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </label>
        )}
        {query.isPending && (
          <div className="flex justify-center py-12 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" aria-label="Loading" />
          </div>
        )}
        {query.isError && <p className="text-sm text-gray-500">Your pages didn&apos;t load. Refresh to try again.</p>}
        {index && settled && index.views.length === 0 && searching && (
          <p className="text-sm text-gray-500">No page titles match &ldquo;{search}&rdquo;.</p>
        )}
        {index && settled && index.views.length === 0 && !searching && (
          <p className="text-sm text-gray-500">No pages yet. Ask your assistant to show you something, like the emails it sent this week.</p>
        )}
        {index && index.views.length > 0 && (
          <>
            <PageSection title="Standard pages" views={index.views.filter((view) => view.default)} />
            <PageSection title="Pages your assistant made" views={index.views.filter((view) => !view.default)} />
          </>
        )}
        {index?.truncated && (
          <p className="text-xs text-gray-500">
            Showing the newest {index.views.length} of {index.total}.{!searching && ' Search to find an older one.'}
          </p>
        )}
      </div>
    </ViewShell>
  )
}
